/**
 * Skilltrees der Heroes (Masterprompt Abschnitt 46).
 *
 * Regeln aus dem Masterprompt:
 * - Jeder Grundskill laesst sich bis Level 10 hoechstens ZWEIMAL verbessern.
 * - Verbesserungen sollen das Spiel veraendern, nicht nur Zahlen erhoehen.
 * - Auf Level 8 wird ein Talent gewaehlt, auf Level 10 ein Capstone.
 * - Kein Respec im Match: Eine Wahl ist endgueltig.
 *
 * Die Wirkungen sind Produktentscheidung vom 24.09.2026, die ZAHLEN sind
 * TUNING_PLACEHOLDER.
 */

/** Eine Verbesserung eines Grundskills. */
export interface SkillUpgradeDefinition {
  id: string;
  heroId: string;
  /** Slot des Skills, den sie verbessert. Das Ultimate hat keine Upgrades. */
  slot: 1 | 2 | 3;
  /** Erste oder zweite Stufe. */
  step: 1 | 2;
  name: string;
  description: string;
}

/** Eine Wahl auf Level 8 oder Level 10. */
export interface HeroChoiceDefinition {
  id: string;
  heroId: string;
  name: string;
  description: string;
}

/** Hoechste Ausbaustufe je Grundskill. */
export const MAX_SKILL_UPGRADE_STEP = 2;

export const SKILL_UPGRADES: readonly SkillUpgradeDefinition[] = [
  // --- Shade (Produktentscheidung vom 25.09.2026) ---
  {
    id: 'whirl_wide',
    heroId: 'shade',
    slot: 1,
    step: 1,
    name: 'Wide Whirl',
    description: 'Blade Whirl covers a much wider area.',
  },
  {
    id: 'whirl_double',
    heroId: 'shade',
    slot: 1,
    step: 2,
    name: 'Second Swing',
    description: 'A moment later it strikes a second time - for 60 percent damage.',
  },
  {
    id: 'step_double',
    heroId: 'shade',
    slot: 2,
    step: 1,
    name: 'Double Step',
    description: 'Right after a leap you may immediately leap a second time.',
  },
  {
    id: 'step_trail',
    heroId: 'shade',
    slot: 2,
    step: 2,
    name: 'Blade Trail',
    description: 'It leaves a trail behind that slows enemies.',
  },
  {
    id: 'smoke_long',
    heroId: 'shade',
    slot: 3,
    step: 1,
    name: 'Lingering Smoke',
    description: 'Smoke Bomb lasts much longer.',
  },
  {
    id: 'smoke_ambush',
    heroId: 'shade',
    slot: 3,
    step: 2,
    name: 'Ambush',
    description: 'Your first hit out of hiding lands twice as hard.',
  },

  // --- Deadeye (Produktentscheidung vom 25.09.2026) ---
  {
    id: 'shot_armorbreak',
    heroId: 'deadeye',
    slot: 1,
    step: 1,
    name: 'Armor Breaker',
    description: 'Piercing Shot ignores part of the target armor.',
  },
  {
    id: 'shot_twin',
    heroId: 'deadeye',
    slot: 1,
    step: 2,
    name: 'Twin Arrows',
    description: 'A second arrow flies slightly offset - for half damage.',
  },
  {
    id: 'trap_wide',
    heroId: 'deadeye',
    slot: 2,
    step: 1,
    name: 'Wide Snare',
    description: 'Snare Trap covers a much wider area.',
  },
  {
    id: 'trap_barbs',
    heroId: 'deadeye',
    slot: 2,
    step: 2,
    name: 'Barbs',
    description: 'Anyone who was held takes more damage for a few seconds after.',
  },
  {
    id: 'barrage_fast',
    heroId: 'deadeye',
    slot: 3,
    step: 1,
    name: 'Rapid Fire',
    description: 'Eight shots instead of six - in the same time.',
  },
  {
    id: 'barrage_steady',
    heroId: 'deadeye',
    slot: 3,
    step: 2,
    name: 'Steady',
    description: 'You take far less damage while firing Barrage.',
  },

  {
    id: 'slam_wide',
    heroId: 'vanguard',
    slot: 1,
    step: 1,
    name: 'Wide Front',
    description: 'Shield Slam covers a cone twice as wide.',
  },
  {
    id: 'slam_weaken',
    heroId: 'vanguard',
    slot: 1,
    step: 2,
    name: 'Concussive',
    description: 'Enemies hit deal far less damage for a moment.',
  },
  {
    id: 'rush_shockwave',
    heroId: 'vanguard',
    slot: 2,
    step: 1,
    name: 'Impact',
    description: 'Shield Rush ends in a blast that slows.',
  },
  {
    id: 'rush_refund',
    heroId: 'vanguard',
    slot: 2,
    step: 2,
    name: 'Rebound',
    description: 'Hit three or more and half the cooldown comes back at once.',
  },
  {
    id: 'ground_field',
    heroId: 'vanguard',
    slot: 3,
    step: 1,
    name: 'Cracked Ground',
    description: 'Groundbreaker leaves three seconds of heavy ground behind.',
  },
  {
    id: 'ground_echo',
    heroId: 'vanguard',
    slot: 3,
    step: 2,
    name: 'Aftershock',
    description: 'Groundbreaker hits a second time - one second later.',
  },

  // --- Arcanist (Produktentscheidung vom 24.09.2026) ---

  {
    id: 'strike_split',
    heroId: 'arcanist',
    slot: 1,
    step: 1,
    name: 'Offshoot',
    description: 'Thornburst also hits a second area behind the first.',
  },
  {
    id: 'strike_mana',
    heroId: 'arcanist',
    slot: 1,
    step: 2,
    name: 'Sapflow',
    description: 'Every enemy hit by Thornburst gives back a bit of mana.',
  },
  {
    id: 'frost_long',
    heroId: 'arcanist',
    slot: 2,
    step: 1,
    name: 'Deep Roots',
    description: 'Bramble Field lasts twice as long.',
  },
  {
    id: 'frost_root',
    heroId: 'arcanist',
    slot: 2,
    step: 2,
    name: 'Rooted',
    description: 'Anyone leaving Bramble Field is rooted for a moment.',
  },
  {
    id: 'wave_wide',
    heroId: 'arcanist',
    slot: 3,
    step: 1,
    name: 'Wide Lash',
    description: 'Lashing Vines cover a much wider cone and push further.',
  },
  {
    id: 'wave_second',
    heroId: 'arcanist',
    slot: 3,
    step: 2,
    name: 'Second Lash',
    description: 'A second lash follows right after Lashing Vines.',
  },
];

/** Talente zur Wahl auf Level 8. */
export const TALENTS: readonly HeroChoiceDefinition[] = [
  {
    id: 'restless',
    heroId: 'shade',
    name: 'Restless',
    description: 'The Momentum you build holds twice as long.',
  },
  {
    id: 'sharp_edge',
    heroId: 'shade',
    name: 'Sharp Edge',
    description: 'Every stack of Momentum also adds damage.',
  },

  {
    id: 'hunter',
    heroId: 'deadeye',
    name: 'Tracker',
    description: 'Marked Prey stacks further: seven stacks instead of five.',
  },
  {
    id: 'eagle_eye',
    heroId: 'deadeye',
    name: 'Eagle Eye',
    description: 'Everything reaches further - skills and your normal attack.',
  },

  {
    id: 'rock',
    heroId: 'vanguard',
    name: 'Bedrock',
    description: 'A lot more health.',
  },
  {
    id: 'wall',
    heroId: 'vanguard',
    name: 'Wall',
    description: 'Your passive reaches full effect at four enemies already.',
  },
  {
    id: 'circle',
    heroId: 'arcanist',
    name: 'Taproot',
    description: 'Sap Surge fills after fewer hits.',
  },
  {
    id: 'clarity',
    heroId: 'arcanist',
    name: 'Springwater',
    description: 'A lot more mana, and it comes back faster.',
  },
];

/** Capstones zur Wahl auf Level 10. */
export const CAPSTONES: readonly HeroChoiceDefinition[] = [
  {
    id: 'twin_shadows',
    heroId: 'shade',
    name: 'Twin Shadows',
    description: 'Shadow Rift summons two shadows instead of one.',
  },
  {
    id: 'frenzy',
    heroId: 'shade',
    name: 'Frenzy',
    description: 'At full Momentum, Blade Whirl costs no energy.',
  },

  {
    id: 'headshot',
    heroId: 'deadeye',
    name: 'Headshot',
    description: 'If Kill Shot kills its target, half the cooldown comes back at once.',
  },
  {
    id: 'second_salvo',
    heroId: 'deadeye',
    name: 'Second Salvo',
    description: 'A short second salvo follows Barrage on its own.',
  },

  {
    id: 'bulwark',
    heroId: 'vanguard',
    name: 'Bulwark',
    description: 'Last Bastion lasts longer and heals when it runs out.',
  },
  {
    id: 'momentum',
    heroId: 'vanguard',
    name: 'Momentum',
    description: 'Every skill hit shortens your ultimate cooldown.',
  },
  {
    id: 'chain',
    heroId: 'arcanist',
    name: 'Second Fall',
    description: 'Deadfall lands a second time - offset and shortly after.',
  },
  {
    id: 'overload',
    heroId: 'arcanist',
    name: 'Full Bloom',
    description: 'A free basic skill from a full bar comes with no cooldown. Not the ultimate.',
  },
];

/**
 * Zahlen zu den Wirkungen. Alles TUNING_PLACEHOLDER.
 *
 * Absichtlich an einer Stelle gebuendelt: Im Balancing wird hier geschraubt,
 * nicht im Code der Simulation.
 */
export const SKILL_TREE_TUNING = {
  /** Wide Front: Faktor auf den Oeffnungswinkel von Shield Slam. */
  slamConeFactor: 2,
  /** Concussive: Getroffene machen so lange so viel Schaden. */
  slamWeaken: { seconds: 3, damageFactor: 0.6 },
  /** Impact: Stosswelle am Ende von Shield Rush. */
  rushShockwave: { radius: 7, damage: 35, slowSeconds: 1.5, slowFactor: 0.6 },
  /** Rebound: Ab so vielen Treffern kommt dieser Anteil der Abklingzeit zurueck. */
  rushRefund: { minHits: 3, ratio: 0.5 },
  /** Cracked Ground: Zaeher Boden nach Groundbreaker. */
  groundField: { seconds: 3, radiusBonus: 1, slowFactor: 0.5 },
  /** Aftershock: Zweiter Einschlag nach dieser Zeit, mit diesem Schadensanteil. */
  groundEcho: { delaySeconds: 1, damageRatio: 0.6 },
  /** Bedrock: Zuschlag auf das maximale Leben. */
  talentRock: { maxHpBonus: 0.2 },
  /** Wall: Ab so vielen Gegnern wirkt die Passive voll. */
  talentWall: { enemiesForMaximum: 4 },
  /** Bulwark: Laengeres Ultimate, das beim Auslaufen heilt. */
  capstoneBulwark: { extraSeconds: 2, healRatio: 0.25 },
  /** Momentum: Abklingzeit des Ultimate je Treffer, und hoechstens je Einsatz. */
  capstoneMomentum: { secondsPerHit: 0.6, maxSecondsPerCast: 4 },

  // --- Arcanist ---

  /** Offshoot: Zweiter Einschlag hinter dem Ziel. */
  strikeSplit: { distance: 6, radiusRatio: 0.7, damageRatio: 0.5 },
  /** Sapflow: So viel Mana je getroffenem Gegner. */
  strikeMana: { manaPerHit: 7 },
  /** Deep Roots: Faktor auf die Standzeit des Bramble Field. */
  frostLongFactor: 2,
  /** Rooted: So lange steht still, wer das Feld verlaesst. */
  frostRoot: { stunSeconds: 0.8 },
  /** Wide Lash: Faktoren auf Oeffnungswinkel und Rueckstoss. */
  waveWide: { coneFactor: 1.6, knockbackFactor: 1.6 },
  /** Second Lash: Zweiter Schlag nach dieser Zeit, mit diesem Schadensanteil. */
  waveSecond: { delaySeconds: 0.5, damageRatio: 0.6 },
  /** Taproot: So viele Treffer fuellen die Anzeige dann. */
  talentCircle: { hitsForFull: 8 },
  /** Springwater: Zuschlag auf Mana und Regeneration. */
  talentClarity: { maxManaBonus: 0.3, regenBonus: 0.5 },
  /** Second Fall: Zweiter Deadfall, versetzt und etwas spaeter. */
  capstoneChain: { distance: 8, delaySeconds: 0.6, damageRatio: 0.6 },

  // --- Deadeye (Produktentscheidung vom 25.09.2026) ---

  /** Armor Breaker: So viel Ruestung laesst der Piercing Shot ausser Acht. */
  shotArmorBreak: { ignoreRatio: 0.5 },
  /** Twin Arrows: Winkel des zweiten Pfeils und sein Schadensanteil. */
  shotTwin: { angleDegrees: 10, damageRatio: 0.5 },
  /** Wide Snare: Faktor auf den Radius des Snare Trap. */
  trapWideFactor: 1.6,
  /** Barbs: So lange zaehlt danach so wenig Ruestung. */
  trapBarbs: { seconds: 4, armorFactor: 0.5 },
  /** Rapid Fire: So viele Schuesse kommen dazu. */
  barrageFast: { extraShots: 2 },
  /** Steady: Schadensreduktion, solange das Barrage laeuft. */
  barrageSteady: { damageReduction: 0.4 },
  /** Tracker: So weit staut sich Marked Prey dann auf. */
  talentHunter: { maxStacks: 7 },
  /** Eagle Eye: Faktor auf alle Reichweiten des Deadeye. */
  talentEagleEye: { rangeFactor: 1.2 },
  /** Headshot: Dieser Anteil der Abklingzeit faellt nach einem Kill weg. */
  capstoneHeadshot: { cooldownRefund: 0.5 },
  /** Second Salvo: So viele Schuesse, so lange, nach dieser Pause. */
  capstoneSecondSalvo: { shots: 3, seconds: 0.9, delaySeconds: 0.4 },

  // --- Shade (Produktentscheidung vom 25.09.2026) ---

  /** Wide Whirl: Faktor auf den Umkreis des Blade Whirl. */
  whirlWideFactor: 1.5,
  /** Second Swing: zweiter Schlag nach dieser Zeit, mit diesem Anteil. */
  whirlDouble: { delaySeconds: 0.4, damageRatio: 0.6 },
  /** Double Step: So lange steht der zweite Sprung bereit. */
  stepDouble: { windowSeconds: 4 },
  /** Blade Trail: So viele Felder auf dem Weg, so lange, mit diesem Tempo. */
  stepTrail: { fields: 3, radius: 2.5, seconds: 3, slowFactor: 0.6 },
  /** Lingering Smoke: So lange haelt die Smoke Bomb dann. */
  smokeLong: { hiddenSeconds: 5 },
  /** Ambush: Faktor auf den ersten Treffer aus dem Versteck. */
  smokeAmbush: { damageFactor: 2 },
  /** Restless: Faktor auf die Haltezeit von Momentum. */
  talentRestlessFactor: 2,
  /** Sharp Edge: Schadenszuschlag je Stufe Momentum. */
  talentSharpEdge: { damagePerStack: 0.05 },
  /** Twin Shadows: So viele Schattenbilder erscheinen dann. */
  capstoneTwinShadows: { shadows: 2 },
} as const;

const UPGRADES_BY_ID = new Map(SKILL_UPGRADES.map((upgrade) => [upgrade.id, upgrade]));

export function getSkillUpgrade(id: string): SkillUpgradeDefinition | undefined {
  return UPGRADES_BY_ID.get(id);
}

/** Alle Verbesserungen eines Skills, aufsteigend nach Stufe. */
export function getSkillUpgrades(heroId: string, slot: number): SkillUpgradeDefinition[] {
  return SKILL_UPGRADES.filter(
    (upgrade) => upgrade.heroId === heroId && upgrade.slot === slot,
  ).sort((a, b) => a.step - b.step);
}

/**
 * Talente bzw. Capstones eines bestimmten Heroes.
 *
 * Bisher gab es nur den Vanguard, deshalb reichten die ungefilterten Listen.
 * Mit dem zweiten Hero muss getrennt werden: Ein Arcanist darf sich nicht aus
 * dem Baum des Vanguard bedienen. Hat ein Hero noch keinen Baum, kommt eine
 * leere Liste zurueck - dann wird ihm auch keine Wahl angeboten.
 */
export function getTalents(heroId: string): HeroChoiceDefinition[] {
  return TALENTS.filter((talent) => talent.heroId === heroId);
}

export function getCapstones(heroId: string): HeroChoiceDefinition[] {
  return CAPSTONES.filter((capstone) => capstone.heroId === heroId);
}

export function getTalent(id: string): HeroChoiceDefinition | undefined {
  return TALENTS.find((talent) => talent.id === id);
}

export function getCapstone(id: string): HeroChoiceDefinition | undefined {
  return CAPSTONES.find((capstone) => capstone.id === id);
}
